import { createContext, useContext } from 'react';
import { useAbility as useCaslAbility } from '@casl/react';
import {
    subject as caslSubject,
    type AppAbility,
    type Subject
} from '@ast-data/shared';

/** The current user's ability (throws if no AbilityProvider above). */
export function useAbility(): AppAbility {
    return useCaslAbility<AppAbility>();
}

export const AbilityReadyContext = createContext(false);

/**
 * Whether the ability reflects the user's real roles yet. Until the profiles
 * replicate, `roles` is `[]` and the ability is indistinguishable from a
 * role-less user's — fine for hiding a button, but NOT for a redirect that
 * decides which app someone lands in. Guard any such decision with this.
 */
export function useAbilityReady(): boolean {
    return useContext(AbilityReadyContext);
}

/**
 * Tag a subject with the fields a scoped rule checks (e.g. projectId), for a
 * scoped `can()` call. CASL's string-literal subjects aren't modelled as tagged
 * objects in TypeScript, so we cast back to `Subject`; the object is still
 * passed to `can` at runtime.
 */
export function scopedSubject(
    type: Subject,
    fields: Record<string, unknown>
): Subject {
    return caslSubject(type as never, fields) as unknown as Subject;
}
